import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clampPersonalVoice, personalVoiceGain } from '@duck/contracts';

/**
 * PERSONAL per-participant voice trims (Discord-style) — for each specific duck I hear, how much quieter or
 * louder I want THEM, just for me. Keyed by the peer's STABLE userId so the choice survives their reconnect
 * (a fresh sessionId still resolves to the same trim), exactly like the voice-Block set.
 *
 * The value is an OFFSET in [−100..+100]: −100 = fully silenced, 0 = unchanged (the default — an absent
 * entry), +100 = doubled. The pure {@link personalVoiceGain} curve turns it into the gain MULTIPLIER the
 * {@link import('../net/voice-client').VoiceClient} layers over the flat per-peer zone gain + the master voice fader.
 *
 * LOCAL & per-device only — never shared, never server-side, never a KPI (Constitution Art. 1: comfort
 * knobs, not surveillance; the trimmed party is never told). Persisted to localStorage like the audio mix +
 * push-to-talk preferences so a turned-down neighbour stays turned down across a refresh. Each duck owns
 * their own ears.
 */

interface PeerVolumeState {
  /** userId → personal offset (−100..+100). Only NON-default (≠ 0) trims are stored; 0 removes the key. */
  volumes: Record<string, number>;
  /** Set (or clear, when 0) one peer's personal trim, keyed by their STABLE userId. */
  setPeerVolume: (userId: string, offset: number) => void;
  /** Reset one peer back to the default (0 = unchanged). */
  resetPeerVolume: (userId: string) => void;
}

export const usePeerVolumeStore = create<PeerVolumeState>()(
  persist(
    (set) => ({
      volumes: {},
      setPeerVolume: (userId, offset) =>
        set((s) => {
          if (!userId) return s;
          const v = clampPersonalVoice(offset);
          if ((s.volumes[userId] ?? 0) === v) return s; // no-op, keep the reference stable
          const next = { ...s.volumes };
          if (v === 0) delete next[userId];
          else next[userId] = v;
          return { volumes: next };
        }),
      resetPeerVolume: (userId) =>
        set((s) => {
          if (!(userId in s.volumes)) return s;
          const next = { ...s.volumes };
          delete next[userId];
          return { volumes: next };
        }),
    }),
    {
      name: 'duck-duck-office-peer-volume',
      version: 1,
      partialize: (s) => ({ volumes: s.volumes }),
    },
  ),
);

/**
 * Map the stored per-peer OFFSETS (−100..+100) to gain MULTIPLIERS (userId → factor, 1 = unchanged) for the
 * VoiceClient. One place turns the UI-facing knob into the audio-facing multiplier so the two never drift.
 */
export function peerGainRecord(volumes: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [userId, offset] of Object.entries(volumes)) out[userId] = personalVoiceGain(offset);
  return out;
}
